// @ts-nocheck
/**
 * SalesforceRelatedList — Salesforce Lightning-style related list table.
 *
 * Renders a card with:
 * - Header: icon, title with count, "Updated X ago" subtitle
 * - Toolbar: gear, refresh, optional "New" button
 * - Table with sortable column headers, numbered rows, row-action dropdowns
 * - "View All" footer link
 */
import React, { useState, useMemo } from 'react';
import { Settings, RefreshCw, ChevronDown } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface RelatedListColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  /** Render custom cell content; falls back to row[key] */
  render?: (row: T, index: number) => React.ReactNode;
  /** Return a plain string for sorting (used instead of raw row[key] when provided) */
  sortValue?: (row: T) => string;
  /** If true, renders as a blue link */
  isLink?: boolean;
  /** Click handler when isLink is true */
  onLinkClick?: (row: T) => void;
  sortable?: boolean;
  width?: string;
}

export interface SalesforceRelatedListProps<T = Record<string, unknown>> {
  /** Section title, e.g. "Credit Block Subscriptions" */
  title: string;
  /** Icon rendered before the title */
  icon?: React.ReactNode;
  /** Icon background colour class */
  iconBg?: string;
  /** Row data */
  rows: T[];
  /** Column definitions */
  columns: RelatedListColumn<T>[];
  /** Max rows shown before "View All" (default 10) */
  pageSize?: number;
  /** Total count to display — defaults to rows.length */
  totalCount?: number;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: boolean;
  /** Callback when "View All" is clicked */
  onViewAll?: () => void;
  /** Callback when "New" button is clicked */
  onNew?: () => void;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Callback when settings is clicked */
  onSettings?: () => void;
  /** Callback for row action dropdown click */
  onRowAction?: (row: T) => void;
  /** Unique key extractor — defaults to row.id */
  rowKey?: (row: T) => string;
  /** Extra class */
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function SalesforceRelatedList<T extends Record<string, unknown>>({
  title,
  icon,
  iconBg = 'bg-purple-600',
  rows,
  columns,
  pageSize = 10,
  totalCount,
  loading = false,
  error = false,
  onViewAll,
  onNew,
  onRefresh,
  onSettings,
  onRowAction,
  rowKey,
  className = '',
}: SalesforceRelatedListProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const displayCount = totalCount ?? rows.length;
  const countLabel = displayCount > pageSize ? `${pageSize}+` : String(displayCount);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    const sorted = [...rows].sort((a, b) => {
      const aVal = col?.sortValue ? col.sortValue(a) : String(a[sortKey] ?? '');
      const bVal = col?.sortValue ? col.sortValue(b) : String(b[sortKey] ?? '');
      return sortDir === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    });
    return sorted;
  }, [rows, columns, sortKey, sortDir]);

  const visibleRows = sortedRows.slice(0, pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const extractKey = (row: T, idx: number): string => {
    if (rowKey) return rowKey(row);
    if (row.id != null) return String(row.id);
    return String(idx);
  };

  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${className}`}>
      {/* ---- Header ---- */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 min-w-0">
          {icon && (
            <span className={`flex items-center justify-center w-7 h-7 rounded ${iconBg} text-white flex-shrink-0`}>
              {icon}
            </span>
          )}
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
              {title} ({countLabel})
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {displayCount} item{displayCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Toolbar buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {onNew && (
            <button
              type="button"
              onClick={onNew}
              className="ml-1 px-3 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              New
            </button>
          )}
        </div>
      </div>

      {/* ---- Loading / Error ---- */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading…</span>
        </div>
      )}

      {error && !loading && (
        <div className="px-4 py-8 text-center text-sm text-red-600 dark:text-red-400">
          Failed to load data.
        </div>
      )}

      {/* ---- Table ---- */}
      {!loading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="w-8 px-1 py-2 text-xs font-normal text-gray-500 dark:text-gray-400 text-center">#</th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 ${
                      col.sortable !== false ? 'cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100' : ''
                    }`}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.sortable !== false && (
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${
                            sortKey === col.key
                              ? sortDir === 'desc'
                                ? 'rotate-180 text-blue-600 dark:text-blue-400'
                                : 'text-blue-600 dark:text-blue-400'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}
                        />
                      )}
                    </span>
                  </th>
                ))}
                {/* Row action column — only render when a handler is wired up */}
                {onRowAction && <th className="w-10 px-2 py-2" />}
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (onRowAction ? 2 : 1)} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No records to display.
                  </td>
                </tr>
              ) : (
                visibleRows.map((row, idx) => (
                  <tr
                    key={extractKey(row, idx)}
                    className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  >
                    <td className="px-1 py-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {idx + 1}
                    </td>
                    {columns.map((col) => {
                      const cellContent = col.render
                        ? col.render(row, idx)
                        : (row[col.key] != null ? String(row[col.key]) : '');

                      return (
                        <td key={col.key} className="px-3 py-2 text-gray-800 dark:text-gray-200">
                          {col.isLink ? (
                            <button
                              type="button"
                              className="text-blue-600 dark:text-blue-400 hover:underline text-left"
                              onClick={() => col.onLinkClick?.(row)}
                            >
                              {cellContent}
                            </button>
                          ) : (
                            cellContent
                          )}
                        </td>
                      );
                    })}
                    {onRowAction && (
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => onRowAction(row)}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 dark:text-gray-500"
                          title="Actions"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- View All footer ---- */}
      {!loading && !error && displayCount > pageSize && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2 text-center">
          <button
            type="button"
            onClick={onViewAll}
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            View All
          </button>
        </div>
      )}
    </div>
  );
}

export default SalesforceRelatedList;
